import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { TableCell, TableRow } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import KOGAutoCompleteCheckbox, { IOption } from "@/main/components/shared/inputAutoCompleteCheck/";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { KeyValue } from "@/main/models/common";
import { getBodyshops, resetReducerBodyshops, useBodyshopReducer } from "@/main/store/bodyshop";
import {
	getBodyshopBranches,
	resetReducerBodyshopBranches,
	useBodyshopBranchReducer,
} from "@/main/store/bodyshopBranch";
import { BodyShopFilterRequest } from "@/main/models/request";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";

import * as SP from "../styles";
import * as S from "./styles";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { getTranslateByKey } from "@/main/services/utils";

interface IBodyshopCompanyInfoProps {
	watcherCountry?: KeyValue | null;
	dataBranchs: IOptionBranch[];
	setDataBranchs: React.Dispatch<React.SetStateAction<IOptionBranch[]>>;
}
export interface IOptionBranch extends IOption {
	nameBranch?: string;
}
function BodyshopCompanyInfo({ watcherCountry, dataBranchs, setDataBranchs }: IBodyshopCompanyInfoProps) {
	const dispatch = useDispatch();

	const bodyshopReducer = useBodyshopReducer();
	const bodyshopBranchReducer = useBodyshopBranchReducer();

	const [optionsSelectedBodyshopOld, setOptionsSelectedBodyshopOld] = useState<KeyValue[]>([]);
	const [optionsSelectedBodyshop, setOptionsSelectedBodyshop] = useState<KeyValue[]>([]);
	const [optionsSelectedBodyshopBranch, setOptionsSelectedBodyshopBranch] = useState<IOptionBranch[]>([]);
	const [errorMessageBodyshop, setErrorMessageBodyshop] = useState<string | null>(null);
	const [errorMessageBodyshopBranch, setErrorMessageBodyshopBranch] = useState<string | null>(null);

	const [optionsBodyshopBranch, setOptionsBodyshopBranch] = useState<IOptionBranch[]>([]);
	const [nameBodyshop, setNameBodyshop] = useState("");

	function validateField(
		value: IOption[],
		setError: React.Dispatch<React.SetStateAction<string | null>>,
		label: string,
	) {
		if (value.length === 0) {
			const messageTranslate = getTranslateByKey("information.selectOne", true);
			const messageError = messageTranslate.replace("{0}", label.toLowerCase());
			setError(messageError);
			return false;
		}

		setError(null);
		return true;
	}
	function validateForm() {
		const isValidBodyshop = validateField(
			optionsSelectedBodyshop,
			setErrorMessageBodyshop,
			getTranslateByKey("information.bodyshop"),
		);
		const isValidBodyshopBranch = validateField(
			optionsSelectedBodyshopBranch,
			setErrorMessageBodyshopBranch,
			getTranslateByKey("information.bodyshopBranch"),
		);

		return isValidBodyshop && isValidBodyshopBranch;
	}
	function handleSubmitBranch(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		if (!validateForm()) return;
		// Check if some bodyshop branch is selected
		const unrelatedHeadquarters = optionsSelectedBodyshop
			.filter((headquarter) => {
				const relatedGroup = optionsSelectedBodyshopBranch.some((item) => item.group === headquarter.value);
				return !relatedGroup;
			})
			.map((item) => item.value);

		if (unrelatedHeadquarters.length > 0) {
			const messageFollowingBodyshopDoNotHaveARelatedBranch = getTranslateByKey(
				"information.theFollowingBodyshopDoNotHaveARelatedBranch",
				true,
			);
			const messageError = `${messageFollowingBodyshopDoNotHaveARelatedBranch}:\n ${unrelatedHeadquarters.join("\n ")}`;
			toast.error(messageError, {
				autoClose: 10000,
				style: {
					whiteSpace: "pre-line",
				},
			});
			return;
		}

		// Check if exists in List
		const branchAlreadyExists: string[] = [];
		const optionsExistsInData = optionsSelectedBodyshopBranch.filter((item) => {
			const hasExists = dataBranchs.find((itemOption) => itemOption.key === item.key);
			if (hasExists) {
				branchAlreadyExists.push(item.value);
			}

			return !hasExists;
		});

		if (branchAlreadyExists.length > 0) {
			const messageFollowingBodyshopBranchAlreadyExists = getTranslateByKey(
				"information.theFollowingBodyshopBranchAlreadyExists",
				true,
			);
			const messageError = `${messageFollowingBodyshopBranchAlreadyExists}:\n ${branchAlreadyExists.join("\n ")}`;
			toast.error(messageError, {
				autoClose: 10000,
				style: {
					whiteSpace: "pre-line",
				},
			});
			return;
		}

		setDataBranchs((prevState) => [...prevState, ...optionsExistsInData]);
		clearStates();
	}
	function handleDeleteBodyshopBranch(key: string) {
		setDataBranchs((prevState) => prevState.filter((item) => item.key !== key));
	}
	function clearStates() {
		setOptionsSelectedBodyshopBranch([]);
		setOptionsSelectedBodyshop([]);
		setOptionsSelectedBodyshopOld([]);
		setOptionsBodyshopBranch([]);
		setNameBodyshop("");
	}

	// LOADERS FUNCTIONS
	function callBodyshopApiDomain(optionSelected: KeyValue | null | undefined) {
		// Clean fields & reducers
		dispatch(resetReducerBodyshops());
		dispatch(resetReducerBodyshopBranches());
		setOptionsSelectedBodyshopBranch([]);
		setOptionsSelectedBodyshopOld([]);

		if (optionSelected) {
			const request: BodyShopFilterRequest = {
				guidCountry: String(optionSelected.key),
				type: HeadQuarterBranchFilterEnum.Only_Headquarters,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getBodyshops(request));
		}
	}

	function callBodyshopBranchApiDomain(companySelected: IOptionBranch | undefined) {
		// Clean fields & reducers
		dispatch(resetReducerBodyshopBranches());

		if (watcherCountry && companySelected !== undefined) {
			const request: BodyShopFilterRequest = {
				guidCountry: String(watcherCountry.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			if (companySelected) {
				request.guidBodyShopHeadQuarter = String(companySelected.key);
			}
			dispatch(getBodyshopBranches(request));
		}
	}

	const orderOptions = (dataOrder: IOptionBranch[]) => {
		if (!dataOrder || dataOrder.length === 0) {
			return [];
		}

		dataOrder.sort((a, b) => {
			if (a.group === undefined || b.group === undefined || b.nameBranch === undefined || a.nameBranch === undefined) {
				return 0;
			}
			if (a.group !== b.group) {
				return a.group < b.group ? -1 : 1;
			}

			if (a.nameBranch === "Headquarters") {
				return -1;
			}
			if (b.nameBranch === "Headquarters") {
				return 1;
			}

			return a.nameBranch.localeCompare(b.nameBranch);
		});

		return dataOrder;
	};

	useEffect(() => {
		clearStates();
		callBodyshopApiDomain(watcherCountry);
	}, [watcherCountry]);

	useEffect(() => {
		if (
			!bodyshopBranchReducer.bodyshopBranches.isLoading &&
			bodyshopBranchReducer.bodyshopBranches.data &&
			bodyshopBranchReducer?.bodyshopBranches?.data?.length > 0
		) {
			const optionsBodyshop = bodyshopBranchReducer.bodyshopBranches.data.reduce((accumulator, item) => {
				const hasExists = optionsBodyshopBranch.some((itemOption) => itemOption.key === item.key);
				if (!hasExists) {
					accumulator.push({
						key: String(item.key),
						value: `${nameBodyshop} - ${item.value}`,
						group: nameBodyshop,
						nameBranch: item.value,
					});
				}
				return accumulator;
			}, [] as IOptionBranch[]);

			setOptionsBodyshopBranch((prev) => [...prev, ...optionsBodyshop]);
		}
	}, [bodyshopBranchReducer.bodyshopBranches.isLoading]);

	const orderedDataBranchs = dataBranchs.length > 0 ? orderOptions(dataBranchs) : [];

	return (
		<S.Container>
			<SP.HeaderSection>
				<SP.HeaderText>{getTranslateByKey("information.bodyshopCompany")}</SP.HeaderText>
			</SP.HeaderSection>
			<form onSubmit={handleSubmitBranch} noValidate>
				<S.RowSelect>
					<S.AutoCompleteField>
						<KOGAutoCompleteCheckbox
							disableVirtualization
							label={getTranslateByKey("information.bodyshop")}
							disableSelectAll
							isLoading={bodyshopReducer.bodyshops.isLoading}
							isFetching={bodyshopBranchReducer.bodyshopBranches.isLoading}
							options={bodyshopReducer.bodyshops.data || []}
							valueCustom={optionsSelectedBodyshopOld}
							setValueCustom={setOptionsSelectedBodyshopOld}
							onChangeCustom={(value, optionChanged) => {
								const optionChangedName = optionChanged?.value;
								validateField(value, setErrorMessageBodyshop, getTranslateByKey("information.bodyshop"));
								// check fields unSelected
								const objectsMissing = optionsSelectedBodyshop.filter((item) => {
									const find = value.find((itemValue) => itemValue.key === item.key);
									return find === undefined;
								});

								if (objectsMissing.length === 0 && optionChangedName !== undefined) {
									const findKey = value.find((item) => item.value === optionChangedName) as IOptionBranch;
									setNameBodyshop(optionChangedName);
									setOptionsSelectedBodyshop(value);
									callBodyshopBranchApiDomain(findKey);
								} else {
									setOptionsBodyshopBranch((prev) => {
										const newOptions = prev.filter((item) => {
											const find = objectsMissing.find((itemValue) => item.group === itemValue.value);
											return find === undefined;
										});
										return newOptions;
									});
									setOptionsSelectedBodyshopBranch((prev) => {
										const newOptions = prev.filter((item) => {
											const find = objectsMissing.find((itemValue) => itemValue.value === item.group);
											return find === undefined;
										});
										return newOptions;
									});

									setOptionsSelectedBodyshop(value);
								}
							}}
							errorMessageCustom={errorMessageBodyshop}
							customInputProps={{
								required: true,
								id: "id-registerInsurer-select-bodyshop",
							}}
						/>
					</S.AutoCompleteField>

					<S.AutoCompleteField>
						<KOGAutoCompleteCheckbox
							disableVirtualization
							label={getTranslateByKey("information.bodyshopBranch")}
							selectAllLabel={getTranslateByKey("information.selectAll")}
							options={orderOptions(optionsBodyshopBranch)}
							isLoading={bodyshopBranchReducer.bodyshopBranches.isLoading}
							valueCustom={optionsSelectedBodyshopBranch}
							setValueCustom={setOptionsSelectedBodyshopBranch}
							onChangeCustom={(value) => {
								validateField(value, setErrorMessageBodyshopBranch, getTranslateByKey("information.bodyshopBranch"));
							}}
							errorMessageCustom={errorMessageBodyshopBranch}
							customInputProps={{
								required: true,
								id: "id-registerInsurer-select-bodyshopBranch",
							}}
						/>
					</S.AutoCompleteField>
					<SecondaryButton
						type="submit"
						style={{
							padding: "0.2rem 0.8rem",
							margin: ".6rem 0 0",
						}}
						id="id-registerInsurer-btn-addBodyshop"
					>
						{getTranslateByKey("information.addBodyshop")}
					</SecondaryButton>
				</S.RowSelect>
			</form>

			{orderedDataBranchs.length > 0 && (
				<BoxGrid
					maxHeight="500px"
					rowHeaders={[
						getTranslateByKey("information.bodyshopBranch"),
						getTranslateByKey("information.bodyshopBranchCompany"),
						getTranslateByKey("information.actions"),
					]}
				>
					{orderedDataBranchs.map((dataBranch) => (
						<TableRow key={dataBranch.key}>
							<TableCell width="30%">{dataBranch.group}</TableCell>
							<TableCell width="65%">{dataBranch.nameBranch}</TableCell>
							<TableCell width="5%" align="center">
								<CloseIcon
									onClick={() => handleDeleteBodyshopBranch(String(dataBranch.key))}
									className="cursor-pointer"
									color="primary"
								/>
							</TableCell>
						</TableRow>
					))}
				</BoxGrid>
			)}
		</S.Container>
	);
}

export default BodyshopCompanyInfo;
